import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import JsBarcode from "jsbarcode";
import Swal from "sweetalert2";
import { baseurl, baseurImage } from "../../Baseurl";
import axiosInstance from "../../api/axiosInstance";

const LABEL_FIELD_OPTIONS = [
  { key: "patientName", label: "Patient Name" },
  { key: "civilId", label: "ID" },
  { key: "phone", label: "Phone" },
  { key: "requestDate", label: "Request Date" },
  { key: "requestId", label: "Lab Request ID" },
  { key: "title", label: "Lab Request Title" },
];

export default function ViewLab() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [labRequest, setLabRequest] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [reportTestValues, setReportTestValues] = useState([]);
  const [showPrintLabelModal, setShowPrintLabelModal] = useState(false);
  const [labelFields, setLabelFields] = useState(() =>
    LABEL_FIELD_OPTIONS.reduce((acc, f) => ({ ...acc, [f.key]: true }), {})
  );

  useEffect(() => {
    axiosInstance
      .get(`getLabRequestById/${id}`)
      .then((res) => { if (res.data.success) setLabRequest(res.data.data); })
      .catch((err) => console.error("Fetch lab request error:", err));

    axiosInstance
      .get(`getLabRequestAttachmentsByLabRequestId/${id}`)
      .then((res) => {
        setAttachments(res.data.success && res.data.data?.length > 0 ? res.data.data : []);
      })
      .catch(() => setAttachments([]));

    axiosInstance
      .get(`getLabReportValues/${id}`)
      .then((res) => { if (res.data.success) setReportTestValues(res.data.data || []); })
      .catch(() => setReportTestValues([]));
  }, [id]);

  const getTestStatus = (value, minRange, maxRange) => {
    if (value === null || value === undefined) return null;
    const v = parseFloat(value);
    if (isNaN(v)) return null;
    const min = minRange !== null && minRange !== undefined ? parseFloat(minRange) : null;
    const max = maxRange !== null && maxRange !== undefined ? parseFloat(maxRange) : null;
    if (min === null && max === null) return "no-range";
    if (min !== null && v < min) return "below";
    if (max !== null && v > max) return "above";
    return "normal";
  };

  const statusConfig = {
    normal:     { background: "#d4edda", color: "#155724", label: "Normal" },
    below:      { background: "#cce5ff", color: "#004085", label: "Below Range" },
    above:      { background: "#f8d7da", color: "#721c24", label: "Above Range" },
    "no-range": { background: "#e2e3e5", color: "#383d41", label: "No Range" },
  };

  const requestStatusStyle = {
    Pending:  { background: "#fff3cd", color: "#856404" },
    Sent:     { background: "#cce5ff", color: "#004085" },
    Received: { background: "#d4edda", color: "#155724" },
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const handlePrintLabel = () => {
    setShowPrintLabelModal(true);
  };

  const toggleLabelField = (key) => {
    setLabelFields((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getLabelFieldValue = (key) => {
    switch (key) {
      case "patientName": return labRequest?.patient_name;
      case "civilId": return labRequest?.patient_civil_id;
      case "phone": return labRequest?.patient_mobile;
      case "requestDate": return formatDate(labRequest?.request_date);
      case "requestId": return labRequest?.request_id ? `#${labRequest.request_id}` : null;
      case "title": return labRequest?.title;
      default: return null;
    }
  };

  const handleConfirmPrintLabel = () => {
    if (!labRequest) return;

    const selectedFields = LABEL_FIELD_OPTIONS
      .filter((f) => labelFields[f.key])
      .map((f) => ({ label: f.label, value: getLabelFieldValue(f.key) }))
      .filter((f) => f.value !== null && f.value !== undefined && f.value !== "");

    let barcodeDataUrl = "";
    if (labRequest.patient_civil_id) {
      try {
        const canvas = document.createElement("canvas");
        JsBarcode(canvas, String(labRequest.patient_civil_id), {
          format: "CODE128",
          displayValue: false,
          margin: 0,
          height: 40,
          width: 1.6,
        });
        barcodeDataUrl = canvas.toDataURL("image/png");
      } catch (err) {
        console.error("Error generating barcode:", err);
      }
    }

    const printWindow = window.open("", "_blank", "width=400,height=300");
    if (!printWindow) return;

    const fieldsHtml = selectedFields
      .map(
        (f) =>
          `<div class="label-row"><span class="label-key">${f.label}:</span> <span class="label-value">${f.value}</span></div>`
      )
      .join("");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Label</title>
          <style>
            @page { size: 2in 1in; margin: 0; }
            * { box-sizing: border-box; }
            body {
              margin: 0;
              width: 2in;
              height: 1in;
              font-family: Arial, Helvetica, sans-serif;
              display: flex;
              flex-direction: column;
              justify-content: center;
              padding: 2px 6px;
              overflow: hidden;
            }
            .label-row {
              font-size: 7.5px;
              line-height: 1.15;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .label-key { font-weight: 700; }
            .barcode-wrap {
              margin-top: 2px;
              text-align: center;
            }
            .barcode-wrap img {
              width: 100%;
              max-height: 30px;
            }
          </style>
        </head>
        <body>
          ${fieldsHtml}
          ${barcodeDataUrl ? `<div class="barcode-wrap"><img src="${barcodeDataUrl}" /></div>` : ""}
        </body>
      </html>
    `);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    };

    setShowPrintLabelModal(false);
  };

  const handleGenerateReport = async () => {
    try {
      const logoRes = await axiosInstance.get("getLogo");
      const logoDataUrl = logoRes.data?.success ? logoRes.data.logo : null;
      const testValues = reportTestValues;

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 14;
      const contentWidth = pageWidth - margin * 2;
      let y = 15;

      const fmtDate = (val) => {
        if (!val) return "—";
        return new Date(val).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
      };

      if (logoDataUrl) {
        try {
          const mimeMatch = logoDataUrl.match(/^data:image\/(\w+);base64,/);
          const imgFormat = mimeMatch ? mimeMatch[1].toUpperCase().replace("JPG", "JPEG") : "PNG";
          const imgProps = doc.getImageProperties(logoDataUrl);
          const maxW = 50, maxH = 25;
          const ratio = Math.min(maxW / imgProps.width, maxH / imgProps.height);
          const imgW = imgProps.width * ratio;
          const imgH = imgProps.height * ratio;
          doc.addImage(logoDataUrl, imgFormat, (pageWidth - imgW) / 2, y, imgW, imgH);
          y += imgH + 5;
        } catch (imgErr) {
          console.warn("Logo render failed:", imgErr);
        }
      }

      doc.setDrawColor(0);
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(0);
      doc.text("LAB TEST REPORT", pageWidth / 2, y, { align: "center" });
      y += 3;
      doc.line(margin, y, pageWidth - margin, y);
      y += 7;

      doc.setFontSize(9.5);
      const col1x = margin;
      const col2x = pageWidth / 2 + 5;
      const labelW = 38;

      const infoLeft = [
        ["Patient Name", labRequest.patient_name || "—"],
        ["Age / Gender", [labRequest.age ? `${labRequest.age} yrs` : null, labRequest.gender].filter(Boolean).join(" / ") || "—"],
        ["CPR (Civil ID)", labRequest.patient_civil_id || "—"],
      ];
      const infoRight = [["Sample Collected At", labRequest.lab_name || "—"]];

      const infoRowCount = Math.max(infoLeft.length, infoRight.length);
      for (let i = 0; i < infoRowCount; i++) {
        let rowHeight = 6;
        if (infoLeft[i]) {
          const [label, value] = infoLeft[i];
          doc.setFont("helvetica", "bold");
          doc.text(`${label}:`, col1x, y);
          doc.setFont("helvetica", "normal");
          if (label === "Patient Name" && value.length > 20) {
            const lines = [];
            for (let s = 0; s < value.length; s += 20) lines.push(value.slice(s, s + 20));
            doc.text(lines, col1x + labelW, y);
            rowHeight = Math.max(rowHeight, 6 + (lines.length - 1) * 5);
          } else {
            doc.text(value, col1x + labelW, y);
          }
        }
        if (infoRight[i]) {
          doc.setFont("helvetica", "bold");
          doc.text(`${infoRight[i][0]}:`, col2x, y);
          doc.setFont("helvetica", "normal");
          doc.text(infoRight[i][1], col2x + labelW, y);
        }
        y += rowHeight;
      }
      y += 4;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("Test Results", margin, y);
      y += 4;

      if (testValues.length > 0) {
        const tableBody = testValues.map((row) => {
          const isText = row.value_type === "text";
          const status = isText ? null : getTestStatus(row.value, row.min_range, row.max_range);
          const isAbnormal = status === "above" || status === "below";
          const valStr = isText
            ? (row.value !== null && row.value !== undefined && row.value !== "" ? String(row.value) : "—")
            : (row.value !== null && row.value !== undefined && row.value !== "" ? parseFloat(row.value).toFixed(2) : "—");
          let refRange;
          if (isText) {
            refRange = row.reference_text || "—";
          } else {
            const minStr = row.min_range !== null && row.min_range !== undefined ? parseFloat(row.min_range).toFixed(2) : "—";
            const maxStr = row.max_range !== null && row.max_range !== undefined ? parseFloat(row.max_range).toFixed(2) : "—";
            refRange = (minStr !== "—" || maxStr !== "—") ? `${minStr} – ${maxStr}` : "—";
          }
          return { cells: [row.test_name, valStr, row.units || "—", refRange, row.methodology || "—"], isAbnormal };
        });

        autoTable(doc, {
          startY: y,
          tableWidth: contentWidth,
          margin: { left: margin, right: margin },
          head: [["Test Name", "Value", "Units", "Ref. Range", "Methodology"]],
          body: tableBody.map((r) => r.cells),
          styles: { fontSize: 9, cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 }, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.2, fillColor: [255, 255, 255] },
          headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold", lineWidth: 0.3, lineColor: [0, 0, 0] },
          alternateRowStyles: { fillColor: [248, 248, 248] },
          columnStyles: {
            0: { cellWidth: 55 }, 1: { cellWidth: 22, halign: "center" },
            2: { cellWidth: 20, halign: "center" }, 3: { cellWidth: 35, halign: "center" },
            4: { cellWidth: 50 },
          },
          didParseCell: (data) => {
            if (data.section === "body" && tableBody[data.row.index]?.isAbnormal)
              data.cell.styles.fontStyle = "bold";
          },
        });
        y = (doc.lastAutoTable?.finalY ?? y) + 8;
      } else {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(9.5);
        doc.setTextColor(80);
        doc.text("No lab test results recorded.", margin, y);
        doc.setTextColor(0);
        y += 10;
      }

      doc.setDrawColor(0);
      doc.setLineWidth(0.2);
      doc.line(margin, y, pageWidth - margin, y);
      y += 5;
      const sampleDates = [
        ["Sample Collected On", fmtDate(labRequest.request_date)],
        ["Sample Received On",  fmtDate(labRequest.pending_at)],
        ["Sample Released On",  fmtDate(labRequest.received_at)],
      ];
      doc.setFontSize(9);
      const dateColW = contentWidth / 3;
      sampleDates.forEach(([label, val], i) => {
        const x = margin + i * dateColW;
        doc.setFont("helvetica", "bold");
        doc.text(label, x, y);
        doc.setFont("helvetica", "normal");
        doc.text(val, x, y + 5);
      });
      y += 14;

      doc.setDrawColor(0);
      doc.setLineWidth(0.3);
      doc.line(margin, y, pageWidth - margin, y);
      y += 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(80);
      const now = new Date().toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
      doc.text(`Generated: ${now}`, margin, y);
      if (labRequest.result_added_by) {
        doc.setFont("helvetica", "bold");
        doc.text(`Done By: ${labRequest.result_added_by}`, pageWidth - margin, y, { align: "right" });
      }

      const pdfBlob = doc.output("blob");
      window.open(URL.createObjectURL(pdfBlob), "_blank");
    } catch (err) {
      console.error("Generate report error:", err);
      Swal.fire("Error", "Failed to generate report. Please try again.", "error");
    }
  };

  if (!labRequest) return <div>Loading...</div>;

  // ── Helpers ──
  const Field = ({ label, value, children }) => (
    <div>
      <div style={{ fontSize: "11px", color: "#8a8a8a", fontWeight: 500, marginBottom: 2, textTransform: "uppercase", letterSpacing: "0.4px" }}>
        {label}
      </div>
      <div style={{ fontSize: "13.5px", fontWeight: 600, color: "#222", lineHeight: 1.4 }}>
        {children || value || <span style={{ color: "#bbb", fontWeight: 400 }}>—</span>}
      </div>
    </div>
  );

  const statusStyle = requestStatusStyle[labRequest.status] || { background: "#e2e3e5", color: "#383d41" };

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div className="col-12">
          <div className="card table-card">

            {/* ── Page Header ── */}
            <div className="tableHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h5 style={{ display: "flex", alignItems: "center", margin: 0, gap: 10 }}>
                <i onClick={() => navigate(-1)} className="fa fa-arrow-left" style={{ cursor: "pointer" }} />
                Lab Request Details
              </h5>
              {reportTestValues.length > 0 && (
                <button
                  className="btn btn-sm"
                  style={{ background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", fontWeight: 600, borderRadius: 6, fontSize: "0.82em" }}
                  onClick={handleGenerateReport}
                >
                  <i className="ti ti-file-text me-1" />Generate Report
                </button>
              )}
            </div>

            <div className="card-body" style={{ padding: "16px 20px" }}>

              {/* ── Section 1: Patient & Request Overview ── */}
              <div style={{ background: "#f8f9fa", borderRadius: 8, padding: "14px 18px", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#4680ff", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                    Patient &amp; Request Info
                  </div>
                  <button
                    type="button"
                    className="borderBtn"
                    style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "5px 12px" }}
                    onClick={handlePrintLabel}
                  >
                    <i className="ti ti-printer" style={{ fontSize: 14 }} />
                    Print Label
                  </button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px 24px" }}>
                  <Field label="Patient Name" value={labRequest.patient_name} />
                  <Field label="Civil ID" value={labRequest.patient_civil_id} />
                  <Field label="Doctor" value={labRequest.doctor_name} />
                  <Field label="Lab" value={labRequest.lab_name} />
                  <Field label="Status">
                    <span style={{ ...statusStyle, borderRadius: 20, padding: "2px 12px", fontSize: 12, fontWeight: 700, display: "inline-block" }}>
                      {labRequest.status || "—"}
                    </span>
                  </Field>
                  <Field label="Request Date" value={formatDate(labRequest.request_date)} />
                  {labRequest.pending_at && <Field label="Sent Date" value={formatDate(labRequest.pending_at)} />}
                  {labRequest.received_at && <Field label="Received Date" value={formatDate(labRequest.received_at)} />}
                </div>
              </div>

              {/* ── Section 2: Request Details ── */}
              {(labRequest.title || labRequest.description || labRequest.sent_by || labRequest.request_time || labRequest.days_since_request) && (
                <div style={{ border: "1px solid #e9ecef", borderRadius: 8, padding: "14px 18px", marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#4680ff", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 12 }}>
                    Request Details
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px 24px" }}>
                    {labRequest.title && <Field label="Title" value={labRequest.title} />}
                    {labRequest.description && (
                      <div style={{ gridColumn: labRequest.title ? "span 2" : "span 3" }}>
                        <Field label="Description" value={labRequest.description} />
                      </div>
                    )}
                    {labRequest.sent_by && <Field label="Sent By" value={labRequest.sent_by} />}
                    {labRequest.request_time && <Field label="Request Time" value={labRequest.request_time} />}
                    {labRequest.days_since_request !== undefined && labRequest.days_since_request !== null && (
                      <Field label="Days Since Request" value={String(labRequest.days_since_request)} />
                    )}
                  </div>
                </div>
              )}

              {/* ── Section 3: Lab Test Results ── */}
              {reportTestValues.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <i className="fas fa-flask" style={{ color: "#4680ff" }} />
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#222" }}>Lab Test Results</span>
                    <span style={{ fontSize: 12, color: "#888", marginLeft: 2 }}>({reportTestValues.length} test{reportTestValues.length !== 1 ? "s" : ""})</span>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-bordered" style={{ fontSize: 13, marginBottom: 0 }}>
                      <thead style={{ background: "#f0f4ff" }}>
                        <tr>
                          <th style={{ fontWeight: 700 }}>Test Name</th>
                          <th style={{ width: 90, textAlign: "center", fontWeight: 700 }}>Value</th>
                          <th style={{ width: 75, textAlign: "center", fontWeight: 700 }}>Units</th>
                          <th style={{ width: 120, textAlign: "center", fontWeight: 700 }}>Ref. Range</th>
                          <th style={{ fontWeight: 700 }}>Methodology</th>
                          <th style={{ width: 110, textAlign: "center", fontWeight: 700 }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportTestValues.map((row) => {
                          const isText = row.value_type === "text";
                          const status = isText ? null : getTestStatus(row.value, row.min_range, row.max_range);
                          const cfg = status ? statusConfig[status] : null;
                          const isAbnormal = status === "above" || status === "below";
                          const valDisplay = isText
                            ? (row.value || "—")
                            : (row.value !== null && row.value !== undefined && row.value !== "" ? parseFloat(row.value).toFixed(2) : "—");
                          const rangeDisplay = isText
                            ? (row.reference_text || "—")
                            : `${row.min_range !== null && row.min_range !== undefined ? parseFloat(row.min_range).toFixed(2) : "—"} – ${row.max_range !== null && row.max_range !== undefined ? parseFloat(row.max_range).toFixed(2) : "—"}`;
                          return (
                            <tr key={row.lab_test_id} style={{ background: isAbnormal ? (cfg?.background + "44") : "transparent" }}>
                              <td style={{ fontWeight: isAbnormal ? 700 : 500 }}>{row.test_name}</td>
                              <td style={{ textAlign: "center", fontWeight: 700, color: cfg ? cfg.color : "#222" }}>{valDisplay}</td>
                              <td style={{ textAlign: "center", color: "#555" }}>{row.units || "—"}</td>
                              <td style={{ textAlign: "center", color: "#6c757d", fontSize: 12 }}>{rangeDisplay}</td>
                              <td style={{ color: "#6c757d" }}>{row.methodology || "—"}</td>
                              <td style={{ textAlign: "center" }}>
                                {cfg ? (
                                  <span style={{ background: cfg.background, color: cfg.color, borderRadius: 10, padding: "3px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                                    {cfg.label}
                                  </span>
                                ) : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── Section 4: Services + Attachments ── */}
              <div style={{ display: "grid", gridTemplateColumns: labRequest.services?.length > 0 && attachments.length > 0 ? "1fr 1fr" : "1fr", gap: 14 }}>

                {/* Services */}
                {labRequest.services?.length > 0 && (
                  <div style={{ border: "1px solid #e9ecef", borderRadius: 8, overflow: "hidden" }}>
                    <div style={{ padding: "10px 14px", background: "#f8f9fa", borderBottom: "1px solid #e9ecef", fontSize: 12, fontWeight: 700, color: "#444" }}>
                      Services ({labRequest.services.length})
                    </div>
                    <div className="table-responsive" style={{ margin: 0 }}>
                      <table className="table m-0" style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th>Service</th>
                            <th>Category</th>
                            <th className="text-end">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {labRequest.services.map((service, idx) => (
                            <tr key={service.serviceId || idx}>
                              <td>{service.serviceName}</td>
                              <td>{service.category}</td>
                              <td className="text-end">{service.price ? `${Number(service.price).toFixed(3)} BHD` : "0.000 BHD"}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan="2" className="text-end" style={{ fontWeight: 600, color: "#444" }}>Total</td>
                            <td className="text-end" style={{ fontWeight: 600 }}>{Number(labRequest.total_price || 0).toFixed(3)} BHD</td>
                          </tr>
                          {Number(labRequest.discount_value) > 0 && (
                            <tr>
                              <td colSpan="2" className="text-end" style={{ fontWeight: 600, color: "#dc3545" }}>
                                Discount ({labRequest.discount_type})
                              </td>
                              <td className="text-end" style={{ fontWeight: 600, color: "#dc3545" }}>
                                -{Number(labRequest.discount_value).toFixed(3)} BHD
                              </td>
                            </tr>
                          )}
                          <tr style={{ background: "#f0f4ff" }}>
                            <td colSpan="2" className="text-end" style={{ fontWeight: 700 }}>Net Amount</td>
                            <td className="text-end" style={{ fontWeight: 700, color: "#3f75c7" }}>
                              {Number(labRequest.net_amount || 0).toFixed(3)} BHD
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )}

                {/* Attachments */}
                <div style={{ border: "1px solid #e9ecef", borderRadius: 8, overflow: "hidden" }}>
                  <div style={{ padding: "10px 14px", background: "#f8f9fa", borderBottom: "1px solid #e9ecef", fontSize: 12, fontWeight: 700, color: "#444" }}>
                    Attachments ({attachments.length})
                  </div>
                  {attachments.length > 0 ? (
                    <div className="table-responsive" style={{ margin: 0 }}>
                      <table className="table m-0" style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>File</th>
                            <th>Uploaded</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attachments.map((att, index) => (
                            <tr key={att.attachment_id || index}>
                              <td>{att.attachment_name}</td>
                              <td>
                                {att.filename ? (
                                  <a href={`${baseurImage}${att.filename}`} target="_blank" rel="noopener noreferrer" style={{ color: "#4680ff" }}>
                                    <i className="ti ti-file me-1" />{att.filename}
                                  </a>
                                ) : (
                                  <span style={{ color: "#bbb" }}>No file</span>
                                )}
                              </td>
                              <td style={{ color: "#555" }}>{formatDate(att.uploaded_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ padding: "16px 14px", color: "#888", fontSize: 13 }}>No attachments found.</div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      {showPrintLabelModal && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 1050,
            background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowPrintLabelModal(false)}
        >
          <div
            style={{
              background: "#fff", borderRadius: 12, width: 360, padding: "24px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)", position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: "#4680ff15", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="ti ti-printer" style={{ color: "#4680ff", fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1a202c" }}>Print Label</div>
                <div style={{ fontSize: 11, color: "#5B6B79" }}>Choose fields to include on the label</div>
              </div>
              <button
                onClick={() => setShowPrintLabelModal(false)}
                style={{
                  marginLeft: "auto", width: 26, height: 26, borderRadius: 6,
                  border: "1px solid #e8ecf0", background: "#f8f9fb", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", color: "#5B6B79", fontSize: 14,
                }}
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
              {LABEL_FIELD_OPTIONS.map((f) => (
                <label
                  key={f.key}
                  style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#2d3748", cursor: "pointer" }}
                >
                  <input
                    type="checkbox"
                    checked={!!labelFields[f.key]}
                    onChange={() => toggleLabelField(f.key)}
                  />
                  {f.label}
                </label>
              ))}
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowPrintLabelModal(false)}
                style={{
                  padding: "7px 16px", fontSize: 13, borderRadius: 8,
                  border: "1px solid #dde3eb", background: "#f8f9fb", color: "#5B6B79", cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPrintLabel}
                className="bgBtn btn-sm d-flex align-items-center gap-1"
                style={{ padding: "7px 16px", fontSize: 13 }}
                disabled={!Object.values(labelFields).some(Boolean)}
              >
                <i className="ti ti-printer" style={{ fontSize: 14 }} />
                Print
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
